import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';

interface EmptyStateProps {
  onSuggestedQuestion?: (question: string) => void;
  suggestedQuestions?: string[];
  isLoadingSuggestions?: boolean;
  chatbotName?: string | null;
}

// i18n keys, resolved at render time so the fallback suggestions follow the UI language.
const DEFAULT_SUGGESTED_QUESTION_KEYS = [
  'chatPage.defaultQuestions.mainUseCases',
  'chatPage.defaultQuestions.createSolution',
  'chatPage.defaultQuestions.indexedDocuments',
  'chatPage.defaultQuestions.extractionProcess',
] as const;

export const EmptyState: React.FC<EmptyStateProps> = ({
  onSuggestedQuestion,
  suggestedQuestions,
  isLoadingSuggestions = false,
  chatbotName,
}) => {
  const { t } = useTranslation();
  const questions = suggestedQuestions && suggestedQuestions.length > 0
    ? suggestedQuestions
    : DEFAULT_SUGGESTED_QUESTION_KEYS.map((key) => t(key));

  return (
    <div className="flex min-h-[min(60vh,520px)] flex-col items-center justify-center px-2 py-12 text-center">
      <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {t('chatPage.emptyWelcome')}
      </h2>
      {chatbotName ? (
        <p className="mb-2 text-sm font-medium text-muted-foreground">{chatbotName}</p>
      ) : null}
      <p className="mb-10 max-w-md text-sm text-muted-foreground sm:text-base">
        {t('chatPage.emptyLead')}
      </p>

      <div className="grid w-full max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
        {isLoadingSuggestions ? (
          <p className="col-span-full text-sm text-muted-foreground">{t('chatPage.loadingSuggestions')}</p>
        ) : (
          questions.map((question, idx) => (
            <Button
              key={idx}
              variant="outline"
              className="h-auto justify-start whitespace-normal break-words rounded-xl border-border/70 bg-muted/20 px-4 py-3 text-left text-sm font-normal leading-snug text-foreground shadow-none hover:border-border hover:bg-muted/50"
              onClick={() => onSuggestedQuestion?.(question)}
            >
              {question}
            </Button>
          ))
        )}
      </div>
    </div>
  );
};

export default EmptyState;
